import { z } from 'zod';
import { localeSchema } from './common';

export const roleSchema = z.enum(['client', 'trainer', 'admin']);
export const genderSchema = z.enum(['female', 'male', 'other', 'undisclosed']);
export const unitSystemSchema = z.enum(['metric', 'imperial']);

/**
 * `GET /v1/me` — profil prijavljenog korisnika.
 *
 * Oblik prati `apps/api/src/routes/me.ts` doslovno. Prije je portal imao svoju,
 * paralelnu definiciju s poljima kojih API nema (`avatarUrl`), pa je demo radio,
 * a produkcija ne. Ovo je sad jedina definicija — i API i klijenti čitaju odavde.
 */
export const meOutput = z.object({
  id: z.string(),
  email: z.string(),
  role: roleSchema,
  locale: localeSchema,
  /** ISO vrijeme potvrde adrese; null = nije potvrđena. */
  emailVerifiedAt: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  birthDate: z.string().nullable(),
  gender: genderSchema.nullable(),
  heightCm: z.number().int().nullable(),
  timezone: z.string(),
  unitSystem: unitSystemSchema,
  /**
   * Ključ u R2, ne URL. Potpisani URL se izdaje zasebno kad ga ekran zatreba —
   * `/v1/me` se čita često, a potpis vrijedi pet minuta.
   */
  avatarKey: z.string().nullable(),
  /**
   * Ime trenera iz aktivne veze u `coaching_relationships`; null dok klijent
   * nema trenera. Profil u portalu ga prikazuje — bez njega klijent nigdje ne
   * vidi tko ga vodi.
   */
  trainerName: z.string().nullable(),
  /** Kanonska definicija pristupa (`entitlements`), izračunata na serveru. */
  hasActiveAccess: z.boolean(),
});

/** `PATCH /v1/me` — sva polja neobavezna, mijenja se samo ono što je poslano. */
export const meUpdateInput = z.object({
  firstName: z.string().min(1).max(80).optional(),
  lastName: z.string().max(80).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  gender: genderSchema.nullable().optional(),
  heightCm: z.number().int().min(100).max(250).nullable().optional(),
  timezone: z.string().max(64).optional(),
  unitSystem: unitSystemSchema.optional(),
  locale: localeSchema.optional(),
});

export type MeOutput = z.infer<typeof meOutput>;
export type MeUpdateInput = z.input<typeof meUpdateInput>;
export type Role = z.infer<typeof roleSchema>;
